import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal, { Sheets, ModalBody, ModalHeader } from "@ingka/modal";
import style from "./createEditServices.module.scss";
import Button from "../ingka/Button";
import upload from "@ingka/ssr-icon/paths/arrow-up-from-base";
import Load from "../ingka/Load";
import { validateFile, validateSpreadsheetContent } from "../../utils/validateFile";
import Text from "../../components/ingka/Text";

const MIN_LOADING_MS = 350;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const waitForUiPaint = () =>
  new Promise((resolve) => {
    const raf =
      (typeof requestAnimationFrame === "function" && requestAnimationFrame) ||
      ((cb) => setTimeout(cb, 16));
    raf(() => raf(resolve));
  });


const UploadFile = ({ visible, title, onClose, onSaveFile }) => {
  const [t] = useTranslation("global");
  const [fileName, setFileName] = useState(null);
  const [file, setFile] = useState(null);
  const [disabledSave, setDisabledSave] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!file) {
      setDisabledSave(true);
      setErrorMessages([]);
      return;
    }

    let isCancelled = false;

    const runValidation = async () => {
      const validationStartedAt = Date.now();
      setDisabledSave(true);
      setErrorMessages([]);
      setIsValidating(true);
      await waitForUiPaint();

      const hasValidExtension = validateFile(file);
      if (!hasValidExtension) {
        if (!isCancelled) {
          const elapsedTime = Date.now() - validationStartedAt;
          if (elapsedTime < MIN_LOADING_MS) {
            await wait(MIN_LOADING_MS - elapsedTime);
          }
          setErrorMessages([
            t("fileValidation.invalidFileType"),
          ]);
          setIsValidating(false);
        }
        return;
      }

      const contentValidationResult = await validateSpreadsheetContent(file, t);

      if (isCancelled) {
        return;
      }

      const elapsedTime = Date.now() - validationStartedAt;
      if (elapsedTime < MIN_LOADING_MS) {
        await wait(MIN_LOADING_MS - elapsedTime);
      }

      setErrorMessages(contentValidationResult.isValid ? [] : contentValidationResult.errors);
      setDisabledSave(!contentValidationResult.isValid);
      setIsValidating(false);
    };

    runValidation();

    return () => {
      isCancelled = true;
    };
  }, [file, t]);

  const openFile = (e) => {
    if (e.target.files.length) {
      const file = e.target.files[0];
      setFile(file);
      setFileName(file.name);
    }
    e.target.value = null;
  };

  const handleClick = () => {
    inputRef.current.click();
  };

  const save = () => {
    onSaveFile(file);
    setFile(null);
    onClose();
  };

  const resetModal = () => {
    setIsValidating(false);
    setFile(null);
    setFileName(null);
    setDisabledSave(true);
    setErrorMessages([]);
    onClose();
  }

  return (
    <Modal
      visible={visible}
      focusLockProps={{
        disabled: false,
      }}
      handleCloseBtn={resetModal}
      qa="modalImport"
    >
      <Sheets
        aria-label="Accessibility header for a modal"
        header={<ModalHeader className={style.modal_header} title={title} />}
      >
        <ModalBody className={style.modal_body}>
          <Button
            type="secondary"
            text={fileName === null ? t("import.uploadPeriodFile") : fileName}
            ssrIcon={upload}
            onClick={handleClick}
            className="col-12"
            qa="buttonSelectFile"
            disabled={isValidating}
          />
          <input
            type="file"
            accept=".xlsx, .xls, .csv"
            style={{ opacity: 0, height: 0 }}
            ref={inputRef}
            onChange={(e) => openFile(e)}
          />
          {isValidating && (
            <div className={style.loading}>
              <Load text={t("fileValidation.reviewingFile")} />
            </div>
          )}
          {errorMessages.length > 0 && (
            <div className={style.fileValidation}>
            <Text tagName="h3" headingSize="s" className={style.title}>{t('fileValidation.title', { errors: errorMessages.length })}</Text>
            <ul>
              {errorMessages.map((error) => (
                <li key={error} className={style.error}>{error}</li>
              ))}
            </ul>
            <Text tagName="p" bodySize="m" className={style.instructions}>{t('fileValidation.instructions')}</Text>
            </div>
          )}
          <Button
            type="emphasised"
            text={t("import.uploadFile")}
            onClick={save}
            className="mt-3 col-12"
            disabled={disabledSave}
            qa="buttonUploadFile"
          />
        </ModalBody>
      </Sheets>
    </Modal>
  );
};

export default UploadFile;
